import {
  createSlice,
  isAnyOf,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit';
import { offensiveTextDetected } from '../../api';
import { PaginatedResponse } from '../../api/PaginatedReponse';
import { ErrorResponse } from '../../api/thunkActionTypes';
import { LikeType } from '../../common/model/LikeType';
import { createComment, createReplyToComment } from '../replies/actions';
import {
  createPost,
  createPostInMultipleRooms,
  deletePost,
  fetchFeedPosts,
  fetchFeedPostsByTopics,
  fetchMyRoomsFeedPosts,
  fetchOpenGraphMetaTags,
  fetchPost,
  fetchRoomPosts,
  moderatorDeletePost,
  moderatorEditPost,
  moderatorPinPost,
  moderatorUnpinPost,
  movePostToAnotherRoom,
  updatePost,
  updatePostLike,
} from './actions';
import { OpenGraphMetaTags, Post } from './models';

const likeUnlikePost = (post: Post, increment: boolean) => {
  if (post.member) {
    post.member.memberLike = increment;
  }
};

const incrementDecrementSentiment = (
  post: Post,
  sentiment: LikeType,
  increment: boolean
) => {
  const incrementValue = increment ? 1 : -1;
  if (
    post.member?.memberLikeValue &&
    post.member?.memberLikeValue !== sentiment
  ) {
    incrementDecrementSentiment(post, post.member?.memberLikeValue, !increment);
  }

  switch (sentiment) {
    case LikeType.like:
      post.likeCount =
        post.likeCount + incrementValue >= 0
          ? post.likeCount + incrementValue
          : post.likeCount;
      break;
    case LikeType.care:
      post.careCount =
        post.careCount + incrementValue >= 0
          ? post.careCount + incrementValue
          : post.careCount;
      break;
    case LikeType.helpful:
      post.helpfulCount =
        post.helpfulCount + incrementValue >= 0
          ? post.helpfulCount + incrementValue
          : post.helpfulCount;
      break;

    default:
      break;
  }
};

const handleFullfilledFetchFeedPostsAction = (
  state: PostState,
  action: PayloadAction<
    PaginatedResponse<Post[]>,
    string,
    {
      arg: {
        xMsContinuationToken?: string | undefined;
        numrecs?: number | undefined;
      };
      requestId: string;
      requestStatus: 'fulfilled';
    },
    never
  >,
  updateMyRoomsList?: boolean
) => {
  // Defensive check: ensure action.payload.data is an array
  const newData = Array.isArray(action.payload.data) ? action.payload.data : [];

  if (updateMyRoomsList) {
    let feedPostsMyRooms = state.feedPostsMyRooms?.data || [];
    if (action.meta.arg.xMsContinuationToken) {
      feedPostsMyRooms.push(...newData);
    } else {
      feedPostsMyRooms = [...newData];
    }
    state.feedPostsMyRooms = {
      data: feedPostsMyRooms,
      xMsContinuationToken: action.payload?.xMsContinuationToken,
    };
    state.loadingFeedPostsMyRooms = false;
  } else {
    let feedPosts = state.feedPosts?.data || [];
    if (action.meta.arg.xMsContinuationToken) {
      feedPosts.push(...newData);
    } else {
      feedPosts = [...newData];
    }
    state.feedPosts = {
      data: feedPosts,
      xMsContinuationToken: action.payload?.xMsContinuationToken,
    };
    state.loadingFeedPosts = false;
  }
};

const handlePendingFetchFeedPostsAction = (
  state: PostState,
  updateMyRoomsList?: boolean
) => {
  if (updateMyRoomsList) {
    state.loadingFeedPostsMyRooms = true;
    state.feedPostsMyRoomsError = undefined;
  } else {
    state.loadingFeedPosts = true;
    state.feedPostsError = undefined;
  }
};

const handleRejectedFetchFeedPostsAction = (
  state: PostState,
  action: PayloadAction<
    ErrorResponse | undefined,
    string,
    {
      arg: {
        xMsContinuationToken?: string | undefined;
        numrecs?: number | undefined;
      };
      requestId: string;
      rejectedWithValue: boolean;
      requestStatus: 'rejected';
      aborted: boolean;
      condition: boolean;
    },
    SerializedError
  >,
  updateMyRoomsList?: boolean
) => {
  if (updateMyRoomsList) {
    state.loadingFeedPostsMyRooms = false;
    state.feedPostsMyRoomsError = action.payload;
  } else {
    state.loadingFeedPosts = false;
    state.feedPostsError = action.payload;
  }
};

export interface PostState {
  roomPosts: PaginatedResponse<Post[]>;
  loadingPosts: boolean;
  feedPosts: PaginatedResponse<Post[]>;
  loadingFeedPosts: boolean;
  feedPostsMyRooms: PaginatedResponse<Post[]>;
  loadingFeedPostsMyRooms: boolean;
  feedPostsMyRoomsError?: ErrorResponse;
  loadingCreateEditPost: boolean;
  createEditPostError?: ErrorResponse;
  focusedPost?: Post;
  focusedPostError?: ErrorResponse;
  loadingFocusedPost?: boolean;
  roomPostsError?: ErrorResponse;
  feedPostsError?: ErrorResponse;
  openGraphMetaTags?: OpenGraphMetaTags;
  loadingOpenGraphMetaTags: boolean;
  fetchingOpenGraphMetaTagsError?: ErrorResponse;
  openMovePostDialog: boolean;
  postToMove?: Post;
}

const initialState: PostState = {
  roomPosts: {
    data: [],
  },
  loadingPosts: false,
  feedPosts: {
    data: [],
  },
  loadingFeedPostsMyRooms: false,
  feedPostsMyRooms: {
    data: [],
  },
  focusedPost: undefined,
  loadingFeedPosts: false,
  loadingCreateEditPost: false,
  createEditPostError: undefined,
  openGraphMetaTags: undefined,
  loadingOpenGraphMetaTags: false,
  openMovePostDialog: false,
  postToMove: undefined,
};

const postSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    clearFeedPosts: (state) => {
      state.feedPosts = {
        data: [],
      };
    },
    clearFeedPostsMyRooms: (state) => {
      state.feedPostsMyRooms = {
        data: [],
      };
    },
    clearRoomPosts: (state) => {
      state.roomPosts = {
        data: [],
      };
    },
    clearFocusedPost: (state) => {
      state.focusedPost = undefined;
    },
    incrementPostReplyCount: (state, action: PayloadAction<string>) => {
      const indexFeed = state.feedPosts.data.findIndex(
        (el) => el.id === action.payload
      );
      const indexFeedMyRooms = state.feedPostsMyRooms.data.findIndex(
        (el) => el.id === action.payload
      );
      const indexRoom = state.roomPosts.data.findIndex(
        (el) => el.id === action.payload
      );
      if (indexFeed !== -1) {
        state.feedPosts.data[indexFeed].replyCount += 1;
      }
      if (indexFeedMyRooms !== -1) {
        state.feedPostsMyRooms.data[indexFeedMyRooms].replyCount += 1;
      }
      if (indexRoom !== -1) {
        state.roomPosts.data[indexRoom].replyCount += 1;
      }
      if (state.focusedPost && state.focusedPost.id === action.payload) {
        state.focusedPost.replyCount += 1;
      }
    },
    decrementPostReplyCount: (state, action: PayloadAction<string>) => {
      const indexFeed = state.feedPosts.data.findIndex(
        (el) => el.id === action.payload
      );
      const indexFeedMyRooms = state.feedPostsMyRooms.data.findIndex(
        (el) => el.id === action.payload
      );
      const indexRoom = state.roomPosts.data.findIndex(
        (el) => el.id === action.payload
      );
      if (indexFeed !== -1) {
        state.feedPosts.data[indexFeed].replyCount -= 1;
      }
      if (indexFeedMyRooms !== -1) {
        state.feedPostsMyRooms.data[indexFeedMyRooms].replyCount -= 1;
      }
      if (indexRoom !== -1) {
        state.roomPosts.data[indexRoom].replyCount -= 1;
      }
      if (state.focusedPost && state.focusedPost.id === action.payload) {
        state.focusedPost.replyCount -= 1;
      }
    },
    decrementPostReplyCountWith: (
      state,
      action: PayloadAction<{ postId: string; decrementCount: number }>
    ) => {
      const indexFeed = state.feedPosts.data.findIndex(
        (el) => el.id === action.payload.postId
      );
      const indexFeedMyPosts = state.feedPostsMyRooms.data.findIndex(
        (el) => el.id === action.payload.postId
      );
      const indexRoom = state.roomPosts.data.findIndex(
        (el) => el.id === action.payload.postId
      );

      if (indexFeed !== -1) {
        state.feedPosts.data[indexFeed].replyCount -=
          action.payload.decrementCount;
      }
      if (indexFeedMyPosts !== -1) {
        state.feedPostsMyRooms.data[indexFeedMyPosts].replyCount -=
          action.payload.decrementCount;
      }
      if (indexRoom !== -1) {
        state.roomPosts.data[indexRoom].replyCount -=
          action.payload.decrementCount;
      }
    },
    clearOpenGraphMetaTags: (state) => {
      state.openGraphMetaTags = undefined;
    },
    setOpenMovePostDialog: (state, action: PayloadAction<boolean>) => {
      state.openMovePostDialog = action.payload;
    },
    setPostToMove: (state, action: PayloadAction<Post | undefined>) => {
      state.postToMove = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoomPosts.pending, (state) => {
        state.loadingPosts = true;
        state.roomPostsError = undefined;
      })
      .addCase(fetchRoomPosts.rejected, (state, action) => {
        state.loadingPosts = false;
        state.roomPostsError = action.payload;
      })
      .addCase(fetchRoomPosts.fulfilled, (state, action) => {
        let roomPosts = state.roomPosts?.data || [];

        // Defensive check: ensure action.payload.data is an array, and not null. Sometimes this happens.
        const newData = Array.isArray(action.payload.data)
          ? action.payload.data
          : [];

        if (action.meta.arg.xMsContinuationToken) {
          roomPosts.push(...newData);
        } else {
          roomPosts = [...newData];
        }
        state.roomPosts = {
          data: roomPosts,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
        state.loadingPosts = false;
      })
      .addCase(createPost.pending, (state) => {
        state.loadingCreateEditPost = true;
        state.createEditPostError = undefined;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loadingCreateEditPost = false;
        state.roomPosts?.data.unshift(action.payload);
      })
      .addCase(createPostInMultipleRooms.pending, (state) => {
        state.loadingCreateEditPost = true;
      })
      .addCase(createPostInMultipleRooms.fulfilled, (state, action) => {
        state.loadingCreateEditPost = false;
      })
      .addCase(createPostInMultipleRooms.rejected, (state) => {
        state.loadingCreateEditPost = false;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loadingCreateEditPost = false;
        state.createEditPostError = action.payload;
      })
      .addCase(updatePostLike.fulfilled, (state, action) => {
        const updatedRoomPosts = state.roomPosts?.data.map((post) =>
          post.id === action.payload.id ? action.payload : post
        );
        const updatedFeedPosts = state.feedPosts?.data.map((post) =>
          post.id === action.payload.id ? action.payload : post
        );
        const updatedFeedPostsMyRooms = state.feedPostsMyRooms?.data.map(
          (post) => (post.id === action.payload.id ? action.payload : post)
        );

        if (state.focusedPost) {
          state.focusedPost = action.payload;
        }

        state.roomPosts.data = updatedRoomPosts;
        state.feedPosts.data = updatedFeedPosts;
        state.feedPostsMyRooms.data = updatedFeedPostsMyRooms;
      })
      .addCase(updatePostLike.pending, (state, action) => {
        const sentiment = action.meta.arg.like.sentiment;
        if (state.focusedPost) {
          const shouldIncrement =
            !(state.focusedPost.member?.memberLikeValue === sentiment);
          incrementDecrementSentiment(
            state.focusedPost,
            sentiment,
            shouldIncrement
          );
          likeUnlikePost(
            state.focusedPost,
            shouldIncrement
          );
          if (state.focusedPost.member) {
            state.focusedPost.member.memberLikeValue = shouldIncrement
              ? sentiment
              : undefined;
          }
        }
        const indexFeed = state.feedPosts.data.findIndex(
          (el) => el.id === action.meta.arg.postId
        );
        const indexFeedMyRooms = state.feedPostsMyRooms.data.findIndex(
          (el) => el.id === action.meta.arg.postId
        );
        const indexRoom = state.roomPosts.data.findIndex(
          (el) => el.id === action.meta.arg.postId
        );

        const feedPost = state.feedPosts.data[indexFeed];
        const feedPostMyRooms = state.feedPostsMyRooms.data[indexFeedMyRooms];
        const roomPost = state.roomPosts.data[indexRoom];

        if (feedPost) {
          const shouldIncrement = !(feedPost.member?.memberLikeValue === sentiment);
          incrementDecrementSentiment(
            feedPost,
            sentiment,
            shouldIncrement
          );
          likeUnlikePost(feedPost, shouldIncrement);
          if (feedPost.member) {
            feedPost.member.memberLikeValue = shouldIncrement
              ? sentiment
              : undefined;
          }
        }

        if (feedPostMyRooms) {
          const shouldIncrement =
            !(feedPostMyRooms.member?.memberLikeValue === sentiment);
          incrementDecrementSentiment(
            feedPostMyRooms,
            sentiment,
            shouldIncrement
          );
          likeUnlikePost(
            feedPostMyRooms,
            shouldIncrement
          );
          if (feedPostMyRooms.member) {
            feedPostMyRooms.member.memberLikeValue = shouldIncrement
              ? sentiment
              : undefined;
          }
        }

        if (roomPost) {
          const shouldIncrement = !(roomPost.member?.memberLikeValue === sentiment);
          incrementDecrementSentiment(
            roomPost,
            sentiment,
            shouldIncrement
          );
          likeUnlikePost(roomPost, shouldIncrement);
          if (roomPost.member) {
            roomPost.member.memberLikeValue = shouldIncrement
              ? sentiment
              : undefined;
          }
        }

        state.feedPosts.data[indexFeed] = feedPost;
        state.feedPostsMyRooms.data[indexFeedMyRooms] = feedPostMyRooms;
        state.roomPosts.data[indexRoom] = roomPost;
      })
      .addCase(fetchFeedPosts.pending, (state) => {
        handlePendingFetchFeedPostsAction(state);
      })
      .addCase(fetchFeedPosts.fulfilled, (state, action) => {
        handleFullfilledFetchFeedPostsAction(state, action);
      })
      .addCase(fetchFeedPosts.rejected, (state, action) => {
        handleRejectedFetchFeedPostsAction(state, action);
      })

      .addCase(fetchPost.fulfilled, (state, action) => {
        state.focusedPost = action.payload;
        state.loadingFocusedPost = false;
      })
      .addCase(fetchPost.pending, (state) => {
        state.loadingFocusedPost = true;
        state.focusedPostError = undefined;
      })
      .addCase(fetchPost.rejected, (state, action) => {
        state.focusedPost = undefined;
        state.focusedPostError = action.payload;
        state.loadingFocusedPost = false;
      })

      .addCase(moderatorDeletePost.pending, (state, action) => {
        state.roomPosts.data = state.roomPosts.data.filter(
          (item) => item.id !== action.meta.arg
        );
        state.feedPosts.data = state.feedPosts.data.filter(
          (item) => item.id !== action.meta.arg
        );
      })
      .addCase(moderatorPinPost.fulfilled, (state, action) => {
        let postToPin = state.roomPosts.data.find(
          (post) => post.id === action.meta.arg.postId
        );
        if (postToPin) {
          postToPin.isPinned = true;
        }
        postToPin = state.feedPosts.data.find(
          (post) => post.id === action.meta.arg.postId
        );
        if (postToPin) {
          postToPin.isPinned = true;
        }
      })
      .addCase(moderatorUnpinPost.fulfilled, (state, action) => {
        let postToUnpin = state.roomPosts.data.find(
          (post) => post.id === action.meta.arg.postId
        );
        if (postToUnpin) {
          postToUnpin.isPinned = false;
        }
        postToUnpin = state.feedPosts.data.find(
          (post) => post.id === action.meta.arg.postId
        );
        if (postToUnpin) {
          postToUnpin.isPinned = false;
        }
      })
      .addCase(deletePost.pending, (state, action) => {
        state.roomPosts.data = state.roomPosts.data.filter(
          (item) => item.id !== action.meta.arg
        );
        state.feedPosts.data = state.feedPosts.data.filter(
          (item) => item.id !== action.meta.arg
        );
      })

      .addCase(fetchOpenGraphMetaTags.pending, (state) => {
        state.loadingOpenGraphMetaTags = true;
        state.fetchingOpenGraphMetaTagsError = undefined;
      })
      .addCase(fetchOpenGraphMetaTags.fulfilled, (state, action) => {
        state.openGraphMetaTags = action.payload;
        state.loadingOpenGraphMetaTags = false;
      })
      .addCase(fetchOpenGraphMetaTags.rejected, (state, action) => {
        state.loadingOpenGraphMetaTags = false;
        state.fetchingOpenGraphMetaTagsError = action.payload;
      })
      .addCase(fetchMyRoomsFeedPosts.fulfilled, (state, action) => {
        handleFullfilledFetchFeedPostsAction(
          state,
          action,
          action.meta.arg.isOnMobile
        );
      })
      .addCase(fetchMyRoomsFeedPosts.pending, (state, action) => {
        handlePendingFetchFeedPostsAction(state, action.meta.arg.isOnMobile);
      })
      .addCase(fetchMyRoomsFeedPosts.rejected, (state, action) => {
        handleRejectedFetchFeedPostsAction(
          state,
          action,
          action.meta.arg.isOnMobile
        );
      })
      .addCase(fetchFeedPostsByTopics.fulfilled, (state, action) => {
        handleFullfilledFetchFeedPostsAction(state, action);
      })
      .addCase(fetchFeedPostsByTopics.pending, (state) => {
        handlePendingFetchFeedPostsAction(state);
      })
      .addCase(fetchFeedPostsByTopics.rejected, (state, action) => {
        handleRejectedFetchFeedPostsAction(state, action);
      })
      .addCase(movePostToAnotherRoom.fulfilled, (state, action) => {
        state.roomPosts.data = state.roomPosts.data.filter(
          (post) => post.id !== action.meta.arg.postId
        );
      })
      .addCase(createComment.rejected, (state, action) => {
        if (offensiveTextDetected(action)) {
          const indexFeed = state.feedPosts.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          const indexFeedMyRooms = state.feedPostsMyRooms.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          const indexRoom = state.roomPosts.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          if (indexFeed !== -1) {
            state.feedPosts.data[indexFeed].replyCount -= 1;
          }
          if (indexFeedMyRooms !== -1) {
            state.feedPostsMyRooms.data[indexFeedMyRooms].replyCount -= 1;
          }
          if (indexRoom !== -1) {
            state.roomPosts.data[indexRoom].replyCount -= 1;
          }
          if (
            state.focusedPost &&
            state.focusedPost.id === action.meta.arg.postId
          ) {
            state.focusedPost.replyCount -= 1;
          }
        }
      })
      .addCase(createReplyToComment.rejected, (state, action) => {
        if (offensiveTextDetected(action)) {
          const indexFeed = state.feedPosts.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          const indexFeedMyRooms = state.feedPostsMyRooms.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          const indexRoom = state.roomPosts.data.findIndex(
            (el) => el.id === action.meta.arg.postId
          );
          if (indexFeed !== -1) {
            state.feedPosts.data[indexFeed].replyCount -= 1;
          }
          if (indexFeedMyRooms !== -1) {
            state.feedPostsMyRooms.data[indexFeedMyRooms].replyCount -= 1;
          }
          if (indexRoom !== -1) {
            state.roomPosts.data[indexRoom].replyCount -= 1;
          }
          if (
            state.focusedPost &&
            state.focusedPost.id === action.meta.arg.postId
          ) {
            state.focusedPost.replyCount -= 1;
          }
        }
      })
      .addMatcher(
        isAnyOf(moderatorEditPost.fulfilled, updatePost.fulfilled),
        (state, action) => {
          const updatedRoomPosts = state.roomPosts?.data.map((post) =>
            post.id === action.payload.id ? action.payload : post
          );

          const updatedFeedPosts = state.feedPosts?.data.map((post) =>
            post.id === action.payload.id ? action.payload : post
          );
          state.loadingCreateEditPost = false;
          if (state.focusedPost) {
            state.focusedPost = action.payload;
          }
          state.roomPosts.data = updatedRoomPosts;
          state.feedPosts.data = updatedFeedPosts;
        }
      )
      .addMatcher(
        isAnyOf(moderatorEditPost.pending, updatePost.pending),
        (state, action) => {
          state.loadingCreateEditPost = true;
          state.createEditPostError = undefined;
        }
      )
      .addMatcher(
        isAnyOf(moderatorEditPost.rejected, updatePost.rejected),
        (state, action) => {
          state.loadingCreateEditPost = false;
          state.createEditPostError = action.payload;
        }
      );
  },
});

export const {
  clearFeedPosts,
  clearFeedPostsMyRooms,
  clearRoomPosts,
  incrementPostReplyCount,
  decrementPostReplyCount,
  decrementPostReplyCountWith,
  clearFocusedPost,
  clearOpenGraphMetaTags,
  setOpenMovePostDialog,
  setPostToMove,
} = postSlice.actions;

export default postSlice.reducer;
